import React from 'react'
import NextImage from 'next/image'
import { useRef } from 'react'
import styles from './styles.module.scss'
import { Document } from '@contentful/rich-text-types'
import htmr from 'htmr'
import HeroBannerAnimatedCopy from './HeroBannerAnimatedCopy'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { isBrowser } from '@helpers/env'
import useIsReactClient from '@helpers/hooks/useIsReactClient'
import { Asset } from 'types/contentful/graphQlApi'

if (isBrowser()) {
  gsap.registerPlugin(ScrollTrigger)
}

type HeroBannerAnimatedProps = {
  title: string
  titleSize: 'S' | 'M' | 'L' | 'XL'
  description: {
    content: Document[]
  }
  image: Asset
}

export default function HeroBannerAnimated({
  title,
  titleSize = 'L',
  description,
  image
}: HeroBannerAnimatedProps) {
  const isTabletUp = useMediaQuery({
    minWidth: BREAKPOINT_SIZES.TABLET_LARGE,
    initialState: false
  })
  const isReactClient = useIsReactClient()
  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const titleText = isReactClient && title ? htmr(title) : null

  useGSAP(
    () => {
      if (isTabletUp) {
        if (titleRef.current) {
          gsap
            .timeline({
              defaults: {
                ease: 'none'
              },
              scrollTrigger: {
                trigger: titleRef.current,
                start: 'clamp(top bottom)',
                scrub: 1
              }
            })
            .to(titleRef.current, { xPercent: -10 }, 0)
        }

        if (imageRef.current) {
          gsap
            .timeline({
              defaults: {
                ease: 'none'
              },
              scrollTrigger: {
                trigger: imageRef.current,
                start: 'clamp(top bottom)',
                scrub: 1
              }
            })
            .to(imageRef.current, { xPercent: 15 }, 0)
        }
      }
    },
    {
      dependencies: [isTabletUp]
    }
  )

  return (
    <div className={styles.HeroBannerAnimated}>
      <div className={styles.HeroBannerAnimated__container}>
        <NextImage
          alt={image.title}
          width="500"
          height="500"
          className={styles.HeroBannerAnimated__image}
          ref={imageRef}
          src={image?.file?.url}
        />

        <h1
          ref={titleRef}
          className={`${styles.HeroBannerAnimated__title} ${
            styles[`titleSize--${titleSize}`]
          }`}
        >
          <span>{titleText}</span>
          {isTabletUp && (
            <>
              <span>{titleText}</span>
              <span>{titleText}</span>
              <span>{titleText}</span>
              <span>{titleText}</span>
            </>
          )}
        </h1>

        <div className={styles.HeroBannerAnimated__copy}>
          <HeroBannerAnimatedCopy copy={description.content} />
        </div>
      </div>
    </div>
  )
}
